import { createApi } from '@reduxjs/toolkit/query/react';
import { IPagedResult, ISearchCriteria } from '../../models/filter';
import { IRecordingConfig, ITenantCreateModel, ITenantCreateResult, ITenantReadModel, RecordingBehaviour } from '../../models/tenant';
import { buildQueryString } from '../../utils/mongoFilterUtils';
import { createRbacBaseQuery, del, pagedResultToCacheTags, post, put } from '../../utils/helpers';

const tenantsApi = createApi({
    reducerPath: 'tenants',
    tagTypes: ['Tenant', 'TenantList', 'RecordingConfig'],
    baseQuery: createRbacBaseQuery(''),
    endpoints: (builder) => ({
        searchTenants: builder.query<IPagedResult<ITenantReadModel>, ISearchCriteria>({
            query: (criteria) => `/tenant?${buildQueryString(criteria, ['Name'])}`,
            providesTags: (result) => pagedResultToCacheTags(result, 'Tenant', 'TenantList'),
        }),
        getTenantById: builder.query<ITenantReadModel, string>({
            query: (tenantId) => `/tenant/${tenantId}`,
            providesTags: (_, __, id) => [{ type: 'Tenant', id }],
        }),
        replaceTenant: builder.mutation<void, ITenantReadModel>({
            query: (tenant) => put(`/tenant/${tenant.id}`, tenant),
            invalidatesTags: (_, __, { id }) => [{ type: 'Tenant', id }],
        }),
        createTenant: builder.mutation<ITenantCreateResult, ITenantCreateModel>({
            // NOTE: this is an AdminInterfaceAdapter endpoint
            query: (tenant) => post('/tenants', tenant),
            invalidatesTags: ['TenantList'],
        }),
        getRecordingConfig: builder.query<RecordingBehaviour, string>({
            query: (tenantId) => `/recording/tenants/${tenantId}/config`,
            providesTags: (_, __, tenantId) => [{ type: 'RecordingConfig', id: tenantId }],
            transformResponse: (value: IRecordingConfig) => value.defaultBehaviour,
        }),
        createRecordingConfig: builder.mutation<RecordingBehaviour, { tenantId: string, recordingBehaviour: RecordingBehaviour }>({
            query: ({ tenantId, recordingBehaviour }) => post(`/recording/tenants/${tenantId}/config`, { defaultBehaviour: recordingBehaviour }),
            invalidatesTags: (_, __, { tenantId }) => [{ type: 'RecordingConfig', id: tenantId }],
            transformResponse: (value: IRecordingConfig) => value.defaultBehaviour,
        }),
        updateRecordingConfig: builder.mutation<RecordingBehaviour, { tenantId: string, recordingBehaviour: RecordingBehaviour }>({
            query: ({ tenantId, recordingBehaviour }) => put(`/recording/tenants/${tenantId}/config`, { defaultBehaviour: recordingBehaviour }),
            invalidatesTags: (_, __, { tenantId }) => [{ type: 'RecordingConfig', id: tenantId }],
            transformResponse: (value: IRecordingConfig) => value.defaultBehaviour,
        }),
        deleteRecordingConfig: builder.mutation<void, string>({
            query: (tenantId) => del(`/recording/tenants/${tenantId}/config`),
            // delete endpoints don't usually invalidate a specific entity but here it's needed to ensure proper update
            invalidatesTags: (_, __, tenantId) => [{ type: 'RecordingConfig', id: tenantId }],
        }),
    }),
});

export default tenantsApi;

export const {
    useSearchTenantsQuery,
    useGetTenantByIdQuery,
    useReplaceTenantMutation,
    useCreateTenantMutation,
    useGetRecordingConfigQuery,
    useCreateRecordingConfigMutation,
    useUpdateRecordingConfigMutation,
    useDeleteRecordingConfigMutation,
} = tenantsApi;